import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Edit2, FileText, Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { usePageHeader } from "contexts/PageHeaderContext";
import {
  deleteDocumentTemplate,
  DocumentTemplate,
  listDocumentTemplates,
} from "data/documentTemplateStore";
import { Button } from "ui/button";
import { Card, CardContent } from "ui/card";
import { Input } from "ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "ui/table";
import Pagination from "ui/pagination";
import ConfirmDialog from "ui/ConfirmDialog";

export const DocumentTemplateMasterPage = () => {
  const navigate = useNavigate();
  const { setPageHeader } = usePageHeader();
  const [templates, setTemplates] = useState(listDocumentTemplates);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [toDelete, setToDelete] = useState<DocumentTemplate | null>(null);
  useEffect(() => {
    setPageHeader({
      title: "Document Templates",
      icon: <FileText className="h-5 w-5" />,
    });
    return () => setPageHeader(null);
  }, [setPageHeader]);
  const filtered = useMemo(
    () =>
      templates.filter((item) =>
        item.type.toLowerCase().includes(search.trim().toLowerCase()),
      ),
    [templates, search],
  );
  const pageRows = filtered.slice((page - 1) * pageSize, page * pageSize);
  const confirmDelete = () => {
    if (!toDelete) return;
    deleteDocumentTemplate(toDelete.id);
    setTemplates(listDocumentTemplates());
    setPage(
      Math.min(page, Math.max(1, Math.ceil((filtered.length - 1) / pageSize))),
    );
    setToDelete(null);
    toast.success("Mock template deleted.");
  };
  return (
    <div className="space-y-4 pb-8">
      <div className="flex flex-col gap-2 sm:flex-row">
        <div className="relative min-w-0 flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            aria-label="Search templates"
            placeholder="Search by template name"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="h-10 pl-9"
          />
        </div>
        <Button
          className="h-10 shrink-0 bg-[#5B9BD5] text-white"
          onClick={() => navigate("/document-template-master/add")}
        >
          <Plus className="mr-2 h-4 w-4" />
          Create New Template
        </Button>
      </div>
      <Card className="overflow-hidden border-sky-200 bg-white">
        <CardContent className="!p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#E3F2FD]">
                <TableHead className="px-4 text-[#5B9BD5]">
                  Template Name
                </TableHead>
                <TableHead className="px-4 text-right text-[#5B9BD5]">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pageRows.map((template) => (
                <TableRow key={template.id}>
                  <TableCell className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 shrink-0 text-[#5B9BD5]" />
                      {template.type}
                    </div>
                  </TableCell>
                  <TableCell className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Edit ${template.type}`}
                        onClick={() =>
                          navigate(
                            `/document-template-master/edit?id=${encodeURIComponent(template.id)}`,
                          )
                        }
                      >
                        <Edit2 className="h-4 w-4 text-[#5B9BD5]" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Delete ${template.type}`}
                        onClick={() => setToDelete(template)}
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {!pageRows.length && (
                <TableRow>
                  <TableCell
                    colSpan={2}
                    className="py-8 text-center text-gray-500"
                  >
                    No templates found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <Pagination
            totalItems={filtered.length}
            currentPage={page}
            itemsPerPage={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            label="templates"
          />
        </CardContent>
      </Card>
      <p className="text-xs text-gray-500">
        Mock data. Changes reset when the page is refreshed.
      </p>
      <ConfirmDialog
        open={!!toDelete}
        onOpenChange={(open) => {
          if (!open) setToDelete(null);
        }}
        title="Delete Document Template"
        description={<>Delete {toDelete?.type}?</>}
        confirmText="Yes, Delete"
        onConfirm={confirmDelete}
      />
    </div>
  );
};
